import type { KeyboardEvent } from 'react';

// Tab and Shift+Tab cycle through the container's editable text fields and Escape hands focus back, so
// a block being written keeps the keyboard until the author leaves it.
const trapFieldFocus = (event: KeyboardEvent<HTMLElement>, onExit: () => void) => {
  const container = event.currentTarget;
  const target = event.target;
  // Portaled dialogs own their focus, even though their events bubble through the block.
  if (!(target instanceof HTMLElement) || !container.contains(target)) return;
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  if (target.getAttribute('aria-pressed') === 'true') return;
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    onExit();
    return;
  }
  if (event.key !== 'Tab') return;
  if (!event.shiftKey && target.hasAttribute('data-advance-on-tab') && target.textContent?.trim()) return;
  const fields = editableFields(container);
  const index = fields.indexOf(target);
  if (index === -1) return;
  const next = fields[(index + (event.shiftKey ? -1 : 1) + fields.length) % fields.length];
  event.preventDefault();
  event.stopPropagation();
  next.focus();
};

const editableFields = (container: HTMLElement) => {
  const elements: HTMLElement[] = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_ELEMENT, {
    acceptNode: node => {
      if (!(node instanceof HTMLElement)) return NodeFilter.FILTER_SKIP;
      const style = getComputedStyle(node);
      if (node.hidden || node.inert || style.display === 'none' || style.visibility === 'hidden') {
        return NodeFilter.FILTER_REJECT;
      }
      if (node.tabIndex < 0 || !node.hasAttribute('data-editable-text-field')) return NodeFilter.FILTER_SKIP;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  while (walker.nextNode()) elements.push(walker.currentNode as HTMLElement);
  return elements;
};

export default trapFieldFocus;
